// Pulsar items in the Action Required inbox (standing-agent-ui "Inbox
// integration"; task 9.6): a proposal batch is its own item type — pulsar name,
// proposal count, summary, expiry — with Review opening the review surface in a
// side panel. A batch of exactly ONE proposal can be decided right here. A
// question is a reply card.

import { useEffect, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Input } from '@progress/kendo-react-inputs';
import type { AttentionItem, Proposal } from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { ArgsView } from '../../components/ArgsView';
import { ApiError } from '../../lib/apiClient';
import { decideProposals, getBatch } from '../../lib/standingAgentApi';
import { QuestionReply } from '../StandingAgents/components/QuestionReply';
import { APPROVER_ONLY_REASON } from '../StandingAgents/pulsarUi';

function SingleProposal({ item, canApprove, onDecided }: { item: AttentionItem; canApprove: boolean; onDecided: () => void }) {
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!item.batchId) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const batch = await getBatch(item.batchId!);
        if (!cancelled) setProposal(batch.proposals.find((p) => p.status === 'pending') ?? null);
      } catch {
        if (!cancelled) setProposal(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.batchId, version]);

  if (!proposal || !item.batchId) return null;

  const decide = async (decision: 'approve' | 'deny') => {
    setBusy(true);
    setError(null);
    try {
      await decideProposals(item.batchId!, {
        decisions: [{ proposalId: proposal.id, decision, reason: decision === 'deny' ? reason.trim() || null : null }],
      });
      onDecided();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'Someone else already decided this.' : err instanceof Error ? err.message : 'That did not work.');
      setVersion((v) => v + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 6 }}>
      <p className="inbox-proposed">
        {proposal.title} — <code>{proposal.tool}</code>
      </p>
      <ArgsView args={proposal.editedArgs ?? proposal.args} label={`Arguments for ${proposal.tool}`} />
      {denying ? (
        <form
          className="inbox-answer"
          style={{ marginTop: 8 }}
          onSubmit={(e) => {
            e.preventDefault();
            void decide('deny');
          }}
        >
          <Input
            style={{ flex: 1 }}
            value={reason}
            onChange={(e) => setReason(String(e.value ?? ''))}
            aria-label="Reason (optional)"
            aria-describedby={`deny-hint-${proposal.id}`}
            placeholder="Reason (optional)"
            maxLength={500}
            autoFocus
          />
          <Button type="submit" disabled={busy}>
            Deny
          </Button>
          <Button type="button" fillMode="flat" onClick={() => setDenying(false)}>
            Cancel
          </Button>
          <span id={`deny-hint-${proposal.id}`} className="inbox-muted" style={{ flexBasis: '100%', fontSize: 12 }}>
            Tell {item.agentName} why — it will remember.
          </span>
        </form>
      ) : (
        <div className="inbox-actions" style={{ marginTop: 8 }}>
          <Button
            type="button"
            aria-disabled={!canApprove || busy ? true : undefined}
            title={!canApprove ? APPROVER_ONLY_REASON : undefined}
            aria-describedby={!canApprove ? `approve-reason-${proposal.id}` : undefined}
            onClick={() => canApprove && !busy && void decide('approve')}
          >
            Approve
          </Button>
          <Button type="button" fillMode="outline" disabled={busy} onClick={() => setDenying(true)}>
            Deny
          </Button>
          {!canApprove && (
            <span id={`approve-reason-${proposal.id}`} className="inbox-muted" style={{ fontSize: 12, alignSelf: 'center' }}>
              {APPROVER_ONLY_REASON}.
            </span>
          )}
        </div>
      )}
      {error && <div className="inbox-error" role="alert">{error}</div>}
    </div>
  );
}

export interface PulsarBatchCardProps {
  item: AttentionItem;
  canApprove: boolean;
  onReview: () => void;
  onChanged: () => void;
}

/** The Review button carries `data-review-batch` so the host can return focus to it. */
export function PulsarBatchCard({ item, canApprove, onReview, onChanged }: PulsarBatchCardProps) {
  return (
    <li className="inbox-card">
      <div className="inbox-card-head">
        <span className="inbox-badge proposals">Proposals</span>
        <span className="inbox-goal">
          {item.agentName} — {item.count} proposal{item.count === 1 ? '' : 's'} to review
        </span>
        <Button
          type="button"
          fillMode="outline"
          onClick={onReview}
          style={{ marginLeft: 'auto' }}
          data-review-batch={item.batchId ?? undefined}
          aria-label={`Review ${item.agentName}'s proposals`}
        >
          Review
        </Button>
      </div>
      <div className="inbox-card-meta">
        <time className="inbox-meta-time" dateTime={item.createdUtc}>
          {formatDateTime(item.createdUtc)}
        </time>
        {item.expiresUtc && <span>· expires {formatDateTime(item.expiresUtc)}</span>}
      </div>
      {item.text && <p className="inbox-prompt">{item.text}</p>}
      {item.count === 1 && <SingleProposal item={item} canApprove={canApprove} onDecided={onChanged} />}
    </li>
  );
}

export function PulsarQuestionCard({ item, onChanged }: { item: AttentionItem; onChanged: () => void }) {
  return (
    <li className="inbox-card">
      <div className="inbox-card-head">
        <span className="inbox-badge input">Question</span>
        <span className="inbox-goal">{item.agentName} asks</span>
      </div>
      <p className="inbox-prompt">{item.text}</p>
      <QuestionReply item={item} onChanged={onChanged} />
    </li>
  );
}
